import { useState } from 'react';
import { Menu, X, Info, Zap, RefreshCw, Download } from 'lucide-react';
import ControlsPanel from '@/components/forms/ControlsPanel';
import Kpis from '@/components/dashboard/Kpis';
import EstimationCard from '@/components/dashboard/EstimationCard';
import RecipientComparison from '@/components/dashboard/RecipientComparison';
import ProjectionChart from '@/components/charts/ProjectionChart';
import SavingsPanel from '@/components/dashboard/SavingsPanel';
import CostProjections from '@/components/dashboard/CostProjections';
import DetailsTable from '@/components/dashboard/DetailsTable';
import ChainStatus from '@/components/dashboard/ChainStatus';
import BandwidthResources from '@/components/dashboard/BandwidthResources';

export default function Dashboard() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Sticky Header */}
      <header className="bg-slate-800/80 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-600 rounded-xl">
                <Zap className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  TRON Fee Optimizer
                </h1>
                <p className="text-slate-400">
                  Smart staking for maximum savings
                </p>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Refresh Data</span>
              </button>
              <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export CSV</span>
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Educational Info Strip */}
      <div className="bg-blue-600/10 border-b border-blue-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-3 text-blue-200">
            <Info className="h-5 w-5" />
            <span className="text-sm font-medium">Did you know?</span>
            <span className="text-sm">
              Energy regenerates daily. If you don't stake enough, TRX is burned
              at current chain rates.
            </span>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Controls and Settings (30-35%) */}
          <div className="lg:col-span-4 space-y-8">
            {/* Controls Panel */}
            <div className="sticky top-32">
              <ControlsPanel />
            </div>
          </div>

          {/* Right Column - KPIs, Charts, and Analysis (65-70%) */}
          <div className="lg:col-span-8 space-y-8">
            {/* KPIs Section */}
            <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
              <Kpis />
            </div>

            {/* Estimation Card */}
            <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
              <EstimationCard />
            </div>

            {/* Bandwidth Resources Section */}
            <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
              <BandwidthResources />
            </div>

            {/* Recipient Comparison Section */}
            <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
              <RecipientComparison />
            </div>

            {/* Chart Section - More Height and Whitespace */}
            <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-white mb-2">
                  Resource Projection Chart
                </h2>
                <p className="text-slate-400">
                  Visualize your resource requirements over time
                </p>
              </div>
              <div className="h-96 w-full">
                <ProjectionChart />
              </div>
            </div>

            {/* Chain Status Section */}
            <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
              <ChainStatus />
            </div>
          </div>
        </div>

        {/* Bottom Section - Full Width Analysis */}
        <div className="mt-12 space-y-8">
          {/* Cost Projections */}
          <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
            <CostProjections />
          </div>

          {/* Savings Panel */}
          <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
            <SavingsPanel />
          </div>

          {/* Details Table */}
          <div className="card p-8 bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
            <DetailsTable />
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed right-0 top-0 h-full w-80 bg-slate-800 shadow-xl">
            <div className="p-6">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-6 right-6 p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
              <ControlsPanel />
              <div className="mt-8">
                <EstimationCard />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
